///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { GtthHistoryService } from '../../logical-topology/engine/history-service';
import { Subscription } from 'rxjs';
import { AbstractInteractiveWindow } from '../abstract-interactive-window';
import { ProjectInteractiveWindowsPopupService } from '../project-interactive-windows-popup.service';
import { ActivatedRoute } from '@angular/router';
import { PARAM_BBOX } from '../../../shared/constants/url-params.constants';
import { GtthProjectInfoViewService } from '../../../layouts/navbar/project-info-view.service';
import { MatDialogRef } from '@angular/material/dialog';

@Component({
    selector: 'winde-disabled-entities-dialog',
    templateUrl: './disabled-entities.component.html',
    styleUrls: ['../project-windows.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GtthDisabledEntitiesComponent extends AbstractInteractiveWindow implements OnInit, OnDestroy {
    public showNodes: boolean;
    public showLinks: boolean;
    public disabledNodes: any[];
    public selectedDisabledNodes: string[];
    public disabledLinks: any[];
    public layers: Record<string, string>;
    protected restoreWithDefaultSize = true;
    private layersChangedSubscriber: Subscription;

    constructor(
        private readonly elementRef: ElementRef,
        public dialogRef: MatDialogRef<GtthDisabledEntitiesComponent>,
        private readonly historyService: GtthHistoryService,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly projectInfoService: GtthProjectInfoViewService
    ) {
        super(elementRef);

        this.layersChangedSubscriber = projectInfoService.layerChangedSubscription().subscribe((value) => {
            this.getDisabledNodesAndLinks(value);
            this.getProjectLayers();
            this.changeDetector.markForCheck();
        });
    }

    getNodeLayerIndex(nodeName): number {
        return this.historyService.logicalTopology.getStationByName(nodeName).getLayerIndex();
    }

    getProjectLayers() {
        if (this.projectInfoService.layersOptions?.length) {
            this.layers = this.projectInfoService.getLayersAsKeyValueObj();
        }
    }

    getDisabledNodesAndLinks(layerIndex: number) {
        this.disabledNodes = this.getDisabledNodeNames(layerIndex);
        this.disabledLinks = this.getDisabledLinks(layerIndex);
    }

    ngOnInit() {
        this.getDisabledNodesAndLinks(this.projectInfoService.activeLayer);
        this.getProjectLayers();

        this.selectedDisabledNodes = [];
        for (const station of this.historyService.logicalTopology.getSelectedStations()) {
            this.selectedDisabledNodes.push(station.getName());
        }

        for (const link of this.historyService.logicalTopology.getSelectedLinks()) {
            const fromNode = link.getFromStation().getName();
            const toNode = link.getToStation().getName();
            this.linkSelected(fromNode, toNode, true);
        }
    }

    ngOnDestroy() {
        if (this.layersChangedSubscriber) {
            this.layersChangedSubscriber.unsubscribe();
            this.layersChangedSubscriber = null;
        }
    }

    nodeCreate(nodeName: string, isEnabled: boolean) {
        const nodeIndex = this.getNodeIndex(this.disabledNodes, nodeName);

        if (nodeIndex === -1 && !isEnabled) {
            this.disabledNodes.push(nodeName);
            this.changeDetector.markForCheck();
        }
    }

    nodeDelete(nodeName: string) {
        const nodeIndex = this.getNodeIndex(this.disabledNodes, nodeName);
        if (nodeIndex !== -1) {
            this.disabledNodes.splice(nodeIndex, 1);
            this.changeDetector.markForCheck();
        }
    }

    nodeNameChanged(oldName: string, newName: string) {
        const nodeIndex = this.getNodeIndex(this.disabledNodes, oldName);
        if (nodeIndex !== -1) {
            this.disabledNodes[nodeIndex] = newName;
        }
        const selectedNodeIndex = this.getNodeIndex(this.selectedDisabledNodes, oldName);
        if (selectedNodeIndex !== -1) {
            this.selectedDisabledNodes[selectedNodeIndex] = newName;
        }

        this.disabledLinks.forEach((link) => {
            if (link.fromNode === oldName) {
                link.fromNode = newName;
            } else if (link.toNode === oldName) {
                link.toNode = newName;
            }
        });
        this.changeDetector.markForCheck();
    }

    nodeEnabled(name: string, enabled: boolean) {
        const nodeIndex = this.getNodeIndex(this.disabledNodes, name);
        if (enabled && nodeIndex !== -1) {
            this.disabledNodes.splice(nodeIndex, 1);
        } else if (!enabled && nodeIndex === -1) {
            this.disabledNodes.push(name);
        }
        this.changeDetector.markForCheck();
    }

    nodeSelected(name: string, selected: boolean) {
        const nodeIndex = this.getNodeIndex(this.selectedDisabledNodes, name);
        if (nodeIndex === -1 && selected) {
            this.selectedDisabledNodes.push(name);
        } else if (nodeIndex !== -1 && !selected) {
            this.selectedDisabledNodes.splice(nodeIndex, 1);
        }
        this.changeDetector.markForCheck();
    }

    linkCreate(fromNode: string, toNode: string, isDisabled: boolean) {
        const linkIndex = this.getLinkIndex(fromNode, toNode);
        if (linkIndex === -1 && isDisabled) {
            this.disabledLinks.push({ fromNode, toNode });
        }
        this.changeDetector.markForCheck();
    }

    linkDelete(fromNode: string, toNode: string) {
        const linkIndex = this.getLinkIndex(fromNode, toNode);
        if (linkIndex !== -1) {
            this.disabledLinks.splice(linkIndex, 1);
        }
        this.changeDetector.markForCheck();
    }

    linkSelected(fromName: string, toName: string, selected: boolean) {
        const linkIndex = this.getLinkIndex(fromName, toName);
        if (linkIndex !== -1) {
            this.disabledLinks[linkIndex].selected = selected;
            this.changeDetector.markForCheck();
        }
    }

    linkDisabled(fromNode: string, toNode: string, isDisabled: boolean) {
        const linkIndex = this.getLinkIndex(fromNode, toNode);
        if (isDisabled && linkIndex === -1) {
            this.disabledLinks.push({ fromNode, toNode });
        } else if (!isDisabled && linkIndex !== -1) {
            this.disabledLinks.splice(linkIndex, 1);
        }
        this.changeDetector.markForCheck();
    }

    nodeLayerChanged(_name: string, _newLayerId: number) {
        this.getDisabledNodesAndLinks(this.projectInfoService.activeLayer);
        this.changeDetector.markForCheck();
    }

    closeModal() {
        this.dialogRef.close('cancel');
    }

    toggleOpenedLists(list: string) {
        switch (list) {
            case 'nodes':
                this.showLinks = false;
                this.showNodes = !this.showNodes;
                break;
            case 'links':
                this.showLinks = !this.showLinks;
                this.showNodes = false;
                break;
        }
    }

    @HostListener('document:keydown', ['$event'])
    onKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape' && !this.historyService.hasSearchMarkers()) {
            this.closeModal();
        }
    }

    designRequired(): boolean {
        return false;
    }

    isRestorable(): boolean {
        return true;
    }

    private getDisabledNodeNames(layer = -1): string[] {
        const disabledNodes = [];
        for (const station of this.historyService.logicalTopology.getStationsIterator()) {
            if (!station.isEnabled()) {
                if (layer === -1 || layer === station.getLayerIndex()) {
                    disabledNodes.push(station.getName());
                }
            }
        }
        return disabledNodes;
    }

    private getDisabledLinks(layer = -1): any[] {
        const disabledLinks = [];
        for (const link of this.historyService.logicalTopology.getLinksIterator()) {
            if (link.getLinkIsDisabled()) {
                if (layer === -1 || layer === link.getLinkLayerIndex()) {
                    disabledLinks.push({
                        fromNode: link.getFromStation().getName(),
                        toNode: link.getToStation().getName(),
                        layer: link.getLinkLayerIndex()
                    });
                }
            }
        }
        return disabledLinks;
    }

    private getNodeIndex(list: string[], nodeName: string): number {
        return list.findIndex((name: string) => {
            return name === nodeName;
        });
    }

    private getLinkIndex(fromNode: string, toNode: string): number {
        return this.disabledLinks.findIndex((link) => {
            return link.fromNode === fromNode && link.toNode === toNode;
        });
    }
}

@Component({
    selector: 'winde-disabled-entities-popup',
    template: '',
    standalone: false
})
export class GtthDisabledEntitiesPopupComponent implements OnInit {
    constructor(
        private readonly interactiveWindowsPopupService: ProjectInteractiveWindowsPopupService,
        private readonly activatedRoute: ActivatedRoute
    ) {}

    ngOnInit() {
        let bboxParams;
        if (this.activatedRoute.snapshot.queryParamMap.has(PARAM_BBOX)) {
            bboxParams = this.activatedRoute.snapshot.queryParamMap
                .get(PARAM_BBOX)
                .split(',')
                .map((value) => parseFloat(value));
        }
        const routerPath = this.activatedRoute.routeConfig.path;
        this.interactiveWindowsPopupService.openInteractiveWindow(
            GtthDisabledEntitiesComponent,
            ProjectInteractiveWindowsPopupService.DISABLED_ENTITIES,
            routerPath,
            bboxParams
        );
    }
}
